import { useImperativeHandle, useMemo, useState } from "react";
import type { Ref } from "react";
import { CatalogItemPrice } from "@common/types/catalogItem";
import { CatalogItemPriceOperation } from "@common/schemas/domains/catalogItem/catalogItemPrice.schema";
import { formatForeignCurrency } from "@common/helpers/currency";
import { View, ViewDensity } from "@common/types/views/views";
import { Button } from "@design-system/Button/Button";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { catalogItemPriceViewConfig } from "./catalogItemPriceViewConfig";
import { getCatalogItemPriceCreatedOnLabel } from "./Cells/PriceCreatedOnCell";
import { getCatalogItemPriceStatusLabel } from "./Cells/PriceStatusCell";
import {
    CatalogItemPriceColumn,
    CatalogItemPriceRow,
    CatalogItemPricesTableCallbacks,
    CatalogItemPricesTableMeta,
    addCreatePriceOperation,
    addDeletePriceOperation,
    buildDraftPriceRow,
    buildWorkingPriceRows,
    mergeUpdatePriceOperation,
    removeCreatePriceOperation,
    updateCreatePriceOperationDescription,
} from "./catalogItemPrices.types";
import styles from "./CatalogItemPricesTable.module.scss";

// Pending ops stay local until CatalogItemFormModal Save includes them in
// the same updateCatalogItem request as the item fields.
export interface CatalogItemPricesTableHandle {
    getPendingOperations: () => CatalogItemPriceOperation[];
}

const pricesView: View = {
    columns: [
        { id: CatalogItemPriceColumn.PRICE },
        { id: CatalogItemPriceColumn.DESCRIPTION },
        { id: CatalogItemPriceColumn.CREATED_ON },
        { id: CatalogItemPriceColumn.STATUS },
        { id: CatalogItemPriceColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.LG,
    },
};

function matchesCatalogItemPriceSearch(
    price: CatalogItemPriceRow,
    search: string,
): boolean {
    if (price.isDraft) {
        return true;
    }
    const query = search.trim().toLowerCase();
    if (!query) {
        return true;
    }
    return [
        formatForeignCurrency(price.unitAmount, price.currency.toUpperCase()),
        String(price.unitAmount),
        price.description,
        getCatalogItemPriceStatusLabel(price),
        getCatalogItemPriceCreatedOnLabel(price),
    ].some((value) => value?.toLowerCase().includes(query));
}

interface Props {
    catalogItemId: string;
    prices: CatalogItemPrice[] | undefined;
    currency: string;
    isOpen: boolean;
    isSaving: boolean;
    initialOperations?: CatalogItemPriceOperation[];
}

export const CatalogItemPricesTable = ({
    catalogItemId,
    prices: initialPrices,
    currency,
    isOpen,
    isSaving,
    initialOperations = [],
    ref,
}: Props & { ref?: Ref<CatalogItemPricesTableHandle> }) => {
    const [wasOpen, setWasOpen] = useState(false);
    const [hydratedItemId, setHydratedItemId] = useState(catalogItemId);
    const [prices, setPrices] = useState<CatalogItemPrice[]>(
        initialPrices ?? [],
    );
    const [isDrafting, setIsDrafting] = useState(false);
    const [shouldFocusDraft, setShouldFocusDraft] = useState(false);
    const [search, setSearch] = useState("");
    const [operations, setOperations] = useState<CatalogItemPriceOperation[]>(
        [],
    );
    if (isOpen && (!wasOpen || hydratedItemId !== catalogItemId)) {
        setWasOpen(true);
        setHydratedItemId(catalogItemId);
        setPrices(initialPrices ?? []);
        setIsDrafting(false);
        setShouldFocusDraft(false);
        setSearch("");
        setOperations(initialOperations);
    } else if (!isOpen && wasOpen) {
        setWasOpen(false);
        setIsDrafting(false);
        setShouldFocusDraft(false);
        setSearch("");
        setOperations([]);
    }

    const rows = useMemo(() => {
        const workingRows = buildWorkingPriceRows(
            catalogItemId,
            prices,
            operations,
        );
        if (!isDrafting) {
            return workingRows;
        }
        return [...workingRows, buildDraftPriceRow(catalogItemId, currency)];
    }, [catalogItemId, currency, isDrafting, operations, prices]);

    const visibleRows = useMemo(
        () =>
            rows.filter((price) =>
                matchesCatalogItemPriceSearch(price, search),
            ),
        [rows, search],
    );

    // Both modes allow staging any number of prices before Save - a new item
    // can start with several prices (e.g. multiple currencies) in one
    // create request, same as an existing item can stage several price
    // creates before an update Save.
    const canAdd = !isSaving && !isDrafting;

    const onSetDefault = (price: CatalogItemPriceRow) => {
        setOperations((current) =>
            mergeUpdatePriceOperation(current, price.id, { isDefault: true }),
        );
    };

    const onArchiveToggle = (price: CatalogItemPriceRow) => {
        setOperations((current) =>
            mergeUpdatePriceOperation(current, price.id, {
                active: !price.active,
            }),
        );
    };

    const onDelete = (price: CatalogItemPriceRow) => {
        setOperations((current) => addDeletePriceOperation(current, price.id));
    };

    const onUpdateDescription = (
        price: CatalogItemPriceRow,
        description: string | null,
    ) => {
        setOperations((current) =>
            price.isStaged
                ? updateCreatePriceOperationDescription(
                      current,
                      price.id,
                      description,
                  )
                : mergeUpdatePriceOperation(current, price.id, {
                      description,
                  }),
        );
        return Promise.resolve(true);
    };

    const onCancelDraft = () => {
        setIsDrafting(false);
        setShouldFocusDraft(false);
    };

    const onCommitDraft = (unitAmount: number) => {
        setOperations((current) =>
            addCreatePriceOperation(current, unitAmount, currency),
        );
        setIsDrafting(false);
        setShouldFocusDraft(false);
        return Promise.resolve();
    };

    const onRemoveStaged = (clientId: string) => {
        setOperations((current) =>
            removeCreatePriceOperation(current, clientId),
        );
    };

    useImperativeHandle(
        ref,
        () => ({ getPendingOperations: () => operations }),
        [operations],
    );

    const callbacks: CatalogItemPricesTableCallbacks = {
        onSetDefault,
        onArchiveToggle,
        onDelete,
        onCommitDraft,
        onCancelDraft,
        onRemoveStaged,
        onUpdateDescription,
    };

    const tableMeta: CatalogItemPricesTableMeta = {
        callbacks,
        options: { isDraftPending: isSaving, shouldFocusDraft },
    };

    return (
        <section
            className={styles.section}
            onKeyDown={(event) => {
                // Nested search lives inside the item <Form>; Enter would
                // otherwise submit the modal.
                if (event.key === "Enter") {
                    event.preventDefault();
                }
            }}
        >
            <h4 className={styles.sectionTitle}>Prices</h4>
            <TablePageHeader
                className={styles.header}
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search prices..."
                searchKeyboardShortcuts={false}
            >
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => {
                        setIsDrafting(true);
                        setShouldFocusDraft(true);
                    }}
                    disabled={!canAdd}
                >
                    Add price
                </Button>
            </TablePageHeader>
            <ViewTable
                data={visibleRows}
                view={pricesView}
                config={catalogItemPriceViewConfig}
                className={styles.table}
                rowBorders
                outerBorder
                initialVisibleRows={Math.max(visibleRows.length, 1)}
                enableKeyboardShortcuts={false}
                enableColumnResizing={false}
                tableMeta={tableMeta}
                getRowClassName={(row: CatalogItemPriceRow) => {
                    if (!row.active && !row.isDraft) {
                        return styles.archivedRow;
                    }
                    return initialOperations.some(
                        (operation) =>
                            operation.op === "create" &&
                            operation.clientId === row.id &&
                            operation.unitAmount === row.unitAmount,
                    )
                        ? styles.suggestedRow
                        : undefined;
                }}
                emptyView={() => (
                    <p className={styles.emptyState}>
                        {search ? "No matching prices" : "No prices yet"}
                    </p>
                )}
            />
        </section>
    );
};
